import {
  VaultFileRevisionKind,
  type UserInDb,
  type VaultFileRevisionInDb,
  type VaultFileSignatureInfoInDb,
  type PreprocessingTaskStatus,
} from '@/api';

/** Minimal owner info for display (matches API UserInDb subset). */
export interface VaultFileOwnerDisplay {
  uid: string;
  full_name: string;
  email: string;
}

/**
 * Structural shape of a vault file as returned by the directory endpoint
 * (`VaultFileInDbOwner`) and search (`VaultFileInDb`). Typed loosely so both
 * fit without a cast.
 */
export interface MappableVaultFile {
  uid: string;
  created_at?: string | null;
  updated_at?: string | null;
  current_revision?: VaultFileRevisionInDb | null;
  owner_user?: UserInDb | null;
  /** True while the file's background conversion into a contract runs. */
  converting?: boolean;
  /** The caller's effective bits on the file; null when not computed. */
  permissions?: number | null;
}

/**
 * Superset of the UI fields both the main vault list (via `vaultStore`) and the
 * file picker (`VaultFileBrowser`) derive from a vault file. Each surface keeps
 * its own `VaultItem` interface and picks the fields it needs from here — this
 * is the single source of truth for *how* a file maps to display fields, so new
 * fields are added once.
 */
export interface MappedVaultFile {
  // Common
  key: string;
  uid: string;
  type: 'file';
  name: string;
  previewStatus: PreprocessingTaskStatus | null;
  signatureInfos: VaultFileSignatureInfoInDb[] | null;
  // Store-oriented
  size: number | undefined;
  /** `updated_at` falling back to `created_at`. */
  lastModified: string | undefined;
  contentType: string | undefined;
  ocrStatus: PreprocessingTaskStatus | null | undefined;
  conversionStatus: PreprocessingTaskStatus | null | undefined;
  revisionUid: string | undefined;
  versionNumber: number | undefined;
  /** A contract-generation draft: its current revision is rendered from a template. */
  isDraft: boolean;
  ownerUser: VaultFileOwnerDisplay | null;
  converting: boolean;
  /** The caller's effective bits (READ=1, WRITE=2, ADMIN=4); null when not computed. */
  permissions: number | null;
  // Picker-oriented
  /** Preview size when a preview exists, else the raw size. */
  sizeBytes: number | null;
  pageCount: number | null;
  fileExtension: string | null;
  hasPreviewFile: boolean;
  ownerName: string | null;
  /** `updated_at` only (no created_at fallback). */
  modifiedTime: string | null;
}

/** Extract a lowercased file extension, or null when there isn't a meaningful one. */
export function getFileExtension(fileName: string): string | null {
  const lastDotIndex = fileName.lastIndexOf('.');
  if (lastDotIndex <= 0 || lastDotIndex === fileName.length - 1) {
    return null;
  }
  return fileName.slice(lastDotIndex + 1).toLowerCase();
}

/** Map a vault file to the superset of display fields used across the app. */
export function mapVaultFileToVaultItem(file: MappableVaultFile): MappedVaultFile {
  const owner = file.owner_user;
  const revision = file.current_revision;
  const fileName = revision?.file_name ?? '';
  const hasPreviewFile = revision?.preview_file_size_b != null;
  const effectiveSizeBytes = revision?.preview_file_size_b ?? revision?.size_b ?? null;

  return {
    key: `file-${file.uid}`,
    uid: file.uid,
    type: 'file',
    name: fileName,
    previewStatus: revision?.preview_status ?? null,
    signatureInfos: revision?.signature_infos ?? null,

    size: revision?.size_b ?? undefined,
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must fall through to the next operand; empty string must normalise to undefined
    lastModified: file.updated_at || file.created_at || undefined,
    contentType: revision?.mime_type,
    ocrStatus: revision?.ocr_status,
    conversionStatus: revision?.conversion_status,
    revisionUid: revision?.uid,
    versionNumber: revision?.version_number,
    isDraft: revision?.kind === VaultFileRevisionKind.TEMPLATED,
    ownerUser: owner ? { uid: owner.uid, full_name: owner.full_name, email: owner.email } : null,
    converting: file.converting ?? false,
    permissions: file.permissions ?? null,

    sizeBytes: effectiveSizeBytes,
    pageCount: null,
    fileExtension: hasPreviewFile ? 'pdf' : getFileExtension(fileName),
    hasPreviewFile,
    ownerName: owner ? owner.full_name || owner.email || null : null,
    modifiedTime: file.updated_at ?? null,
  };
}
